import { useState, useEffect } from 'react';

/**
 * Custom hook for accessing user's geolocation
 * @returns {Object} { coordinates, error, loading, requestPermission, permissionState }
 */
export function useGeolocation() {
  const [coordinates, setCoordinates] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt' | 'granted' | 'denied'

  // Check permission state on mount
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionState(result.state);

        // Listen for permission changes
        result.addEventListener('change', () => {
          setPermissionState(result.state);
        });
      }).catch(() => {
        // If query fails, assume prompt state
        setPermissionState('prompt');
      });
    }
  }, []);

  /**
   * Request user's current location
   * @param {Object} options - Geolocation API options
   */
  const requestPermission = (options = {}) => {
    if (!navigator.geolocation) {
      setError({ code: 0, message: 'Geolocation is not supported by your browser' });
      return;
    }

    setLoading(true);
    setError(null);

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
      ...options,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
        setLoading(false);
        setPermissionState('granted');
      },
      (err) => {
        let errorMessage = 'Unable to retrieve your location';

        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            setPermissionState('denied');
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case err.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred while retrieving location.';
        }

        setError({ code: err.code, message: errorMessage });
        setLoading(false);
      },
      defaultOptions
    );
  };

  /**
   * Clear current location data
   */
  const clearLocation = () => {
    setCoordinates(null);
    setError(null);
  };

  return {
    coordinates,
    error,
    loading,
    permissionState,
    requestPermission,
    clearLocation,
  };
}

export default useGeolocation;
